import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import AuthGuard from 'utils/route-guard/AuthGuard';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';
import AdminContainer from 'ui-component/AdminContainer';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { SET_MENU } from 'store/actions';
import { adminTokens } from 'ui-component/admin/AdminPage';

// assets
import { IconChevronRight } from '@tabler/icons-react';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  boxSizing: 'border-box',
  backgroundColor: adminTokens.bg,
  minHeight: '100vh',
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    ['padding-left', 'width'],
    open
      ? {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }
      : {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }
  ),
  [theme.breakpoints.up('md')]: {
    marginLeft: 0,
    marginRight: 0,
    width: 'auto',
    maxWidth: '100%',
    flexGrow: 1,
    minWidth: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    overflowX: 'hidden'
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    maxWidth: '100%',
    padding: 0,
    overflowX: 'hidden'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    maxWidth: '100%',
    padding: 0,
    overflowX: 'hidden'
  }
}));

const adminOutletSx = {
  minHeight: 'calc(100vh - 72px)',
  bgcolor: adminTokens.bg,
  '& > :not(.zhilv-admin-page):not(.zhilv-hotel-page)': {
    px: { xs: 2, sm: 3, md: 3.5, lg: 4 }
  },
  '& > :first-of-type:not(.zhilv-admin-page):not(.zhilv-hotel-page)': {
    pt: { xs: 2, sm: 3, md: 3.5, lg: 4 }
  },
  '& > :last-child:not(.zhilv-admin-page):not(.zhilv-hotel-page)': {
    pb: { xs: 2, sm: 3, md: 3.5, lg: 4 }
  },
  '& .MuiCard-root': {
    border: `1px solid ${adminTokens.border}`,
    borderRadius: '6px',
    boxShadow: 'none',
    backgroundImage: 'none'
  },
  '& .MuiTypography-h4': {
    color: adminTokens.ink,
    fontSize: 22,
    lineHeight: 1.25,
    fontWeight: 750
  },
  '& .MuiButton-containedPrimary': {
    backgroundColor: `${adminTokens.accent} !important`,
    color: '#FFFFFF',
    boxShadow: 'none',
    borderRadius: '4px',
    minHeight: 40,
    fontWeight: 650,
    textTransform: 'none'
  },
  '& .MuiButton-containedPrimary:hover': {
    backgroundColor: `${adminTokens.accentHover} !important`,
    boxShadow: 'none'
  },
  '& .MuiButton-outlinedPrimary, & .MuiButtonGroup-grouped': {
    borderColor: `${adminTokens.borderStrong} !important`,
    color: adminTokens.accent,
    borderRadius: '4px',
    minHeight: 36,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none'
  },
  '& .MuiButton-outlinedPrimary:hover, & .MuiButtonGroup-grouped:hover': {
    borderColor: `${adminTokens.accent} !important`,
    backgroundColor: adminTokens.hover
  },
  '& .MuiToolbar-root': {
    minHeight: 58,
    height: 'auto',
    px: { xs: 2, sm: 3 },
    py: 1.5
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: adminTokens.surface
  },
  '& .MuiOutlinedInput-root fieldset': {
    borderColor: adminTokens.borderStrong
  },
  '& .MuiOutlinedInput-root:hover fieldset': {
    borderColor: adminTokens.accent
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: adminTokens.accent,
    borderWidth: 1
  },
  '& .MuiTableCell-head': {
    backgroundColor: adminTokens.surfaceAlt,
    color: '#53645E',
    fontSize: 13,
    fontWeight: 700,
    borderBottom: `1px solid ${adminTokens.border}`
  },
  '& .MuiTableCell-body': {
    borderBottom: `1px solid ${adminTokens.border}`,
    color: adminTokens.ink
  },
  '& .MuiTableRow-root:hover .MuiTableCell-body': {
    backgroundColor: adminTokens.hover
  },
  '& .MuiTablePagination-root': {
    borderTop: `1px solid ${adminTokens.border}`,
    color: adminTokens.muted
  },
  '& .MuiTabs-root': {
    minHeight: 48
  },
  '& .MuiTab-root': {
    minHeight: 48,
    textTransform: 'none',
    fontWeight: 650
  },
  '& .MuiTab-root.Mui-selected': {
    color: adminTokens.accent
  },
  '& .MuiTabs-indicator': {
    backgroundColor: adminTokens.accent
  },
  '& .MuiChip-root': {
    borderRadius: '4px',
    fontWeight: 600
  }
};

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const currentUser = useSelector((state) => state.account.user);
  const isHotel = currentUser?.biz_role === 'hotel';
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };

  useEffect(() => {
    dispatch({ type: SET_MENU, opened: !matchDownMd });
  }, [dispatch, matchDownMd]);

  return (
    <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: adminTokens.surface,
          borderBottom: `1px solid ${adminTokens.border}`,
          transition: theme.transitions.create(['left', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
          })
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: '64px !important', md: '72px !important' },
            height: { xs: 64, md: 72 },
            px: '0 !important',
            py: '0 !important'
          }}
        >
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main theme={theme} open={leftDrawerOpened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign card={false} divider={false} />
        <AuthGuard>
          <AdminContainer maxWidth={false} disableGutters>
            <Box sx={isHotel ? undefined : adminOutletSx}>
              <Outlet />
            </Box>
          </AdminContainer>
        </AuthGuard>
      </Main>
    </Box>
  );
};

export default MainLayout;
